import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Mail, MessageSquare, Phone, ShieldCheck, Sparkles, Store } from 'lucide-react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { fmtNumber } from '../lib/localeUtils';

const ICONS = { ai: Sparkles, sms: MessageSquare, email: Mail };
const TOPICS = [['add_ons', 'Add-ons (AI, SMS, Email)'], ['plan', 'Plan or renewal'],
  ['kyc', 'Pharmacy verification (KYC)'], ['branches', 'Branches'], ['other', 'Something else']];

const when = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

export default function StandingPage() {
  const [data, setData] = useState(null);
  const [requests, setRequests] = useState([]);
  const [open, setOpen] = useState(false);
  const [topic, setTopic] = useState('add_ons');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get('/pharmacy/standing').then(r => setData(r.data))
      .catch(e => toast.error(e?.response?.data?.detail || 'Could not load your standing'));
    api.get('/pharmacy/support-requests').then(r => setRequests(r.data.requests || [])).catch(() => setRequests([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const send = async () => {
    setBusy(true);
    try {
      await api.post('/pharmacy/support-request', { topic, message });
      toast.success('Sent to DutiLyne — we will come back to you');
      setOpen(false); setMessage(''); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not send the request'); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="standing-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Your DutiLyne standing
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            A read-only summary of your plan, allowances and verification. DutiLyne manages changes for you.
          </p>
        </div>
        <Button className="gap-2" onClick={() => setOpen(true)} data-testid="contact-dutilyne-btn">
          <Phone className="h-4 w-4" /> Contact DutiLyne
        </Button>
      </div>

      <Card className="bg-muted/40" data-testid="standing-strip">
        <CardContent className="py-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
          {[
            ['Plan', data.plan.tier ? `${data.plan.tier}${data.plan.cycle ? ` · ${data.plan.cycle}` : ''}` : 'No plan on file', data.plan.status],
            ['Renews', when(data.plan.renews_at), data.plan.cancel_at_period_end ? 'cancels at period end' : ''],
            ['Verification', data.kyc_status, data.publicly_listed ? 'listed publicly' : 'not listed yet'],
            ['Branches', `${data.branches.used} of ${data.branches.limit}`,
              data.branches.pending ? `${data.branches.pending} awaiting approval`
                : data.branches.extra_slots ? `incl. ${data.branches.extra_slots} paid at GHS ${data.branches.slot_price_ghs}/mo`
                : data.branches.tier_name],
            ['Pharmacy status', data.pharmacy_status, data.trial_ends_at ? `trial ends ${when(data.trial_ends_at)}` : ''],
          ].map(([label, value, hint]) => (
            <div key={label} data-testid={`standing-${label.toLowerCase().replace(/\s+/g, '-')}`}>
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
              <p className="text-base font-semibold capitalize mt-0.5 break-words">{value || '—'}</p>
              {hint ? <p className="text-[11px] text-muted-foreground capitalize">{hint}</p> : null}
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-3">
        {data.allowances.map(a => {
          const Icon = ICONS[a.kind] || Sparkles;
          return (
            <Card key={a.kind} className={a.exhausted ? 'border-destructive/50' : ''} data-testid={`standing-addon-${a.kind}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2"><Icon className="h-4 w-4 text-primary" /> {a.label}</span>
                  {a.disabled
                    ? <Badge variant="destructive" className="text-[10px]">Off</Badge>
                    : <Badge variant="outline" className="text-[10px] tabular-nums" data-testid={`standing-remaining-${a.kind}`}>{fmtNumber(a.remaining)} left</Badge>}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Progress value={Math.min(100, a.pct_used)} className="h-2" />
                <p className="text-xs text-muted-foreground tabular-nums">
                  Plan includes <b>{fmtNumber(a.included)}</b> this month · used <b>{fmtNumber(a.used)}</b>
                </p>
                {a.exhausted && (
                  <p className="text-xs text-destructive font-medium" data-testid={`standing-exhausted-${a.kind}`}>
                    Used up for this month — ask DutiLyne for a top-up.
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card data-testid="standing-requests">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-primary" /> Your requests to DutiLyne
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {requests.length === 0 && <p className="text-sm text-muted-foreground" data-testid="standing-no-requests">Nothing raised yet.</p>}
          {requests.map(r => (
            <div key={r.request_id} className="rounded-lg border p-3 space-y-1" data-testid={`standing-request-${r.request_id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <p className="text-sm font-medium capitalize">{(r.topic || '').replace(/_/g, ' ')}</p>
                <Badge variant="outline" className="text-[10px] capitalize">{r.status}</Badge>
              </div>
              <p className="text-xs text-muted-foreground whitespace-pre-wrap break-words">{r.message}</p>
              {(r.notes || []).map((n, i) => (
                <p key={i} className="text-[11px] text-sky-900 bg-sky-50 border border-sky-200 rounded px-2 py-1">
                  DutiLyne: “{n.note}”
                </p>
              ))}
              <p className="text-[11px] text-muted-foreground">Raised {when(r.created_at)}</p>
            </div>
          ))}
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground flex items-center gap-1.5">
        <Store className="h-3.5 w-3.5" /> Closer to your patients, Connected to your Pharmacy.
      </p>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="contact-dutilyne-dialog">
          <DialogHeader>
            <DialogTitle>Contact DutiLyne</DialogTitle>
            <DialogDescription>Your pharmacy, plan and contact details come through with the request.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">What is this about</Label>
              <select className="w-full h-9 rounded-md border bg-background px-2 text-sm" value={topic}
                onChange={e => setTopic(e.target.value)} data-testid="contact-topic">
                {TOPICS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Message</Label>
              <Textarea rows={4} value={message} onChange={e => setMessage(e.target.value)}
                placeholder="For example: we need 2,000 more SMS credits this month" data-testid="contact-message" />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={send} disabled={busy || message.trim().length < 10} data-testid="contact-send">
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Send request
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
